import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { Divider } from '@material-ui/core';
import { IoIosArrowBack } from 'react-icons/io';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErrors';

import Input from '../../newComponents/UnformInput';
import Select, { SelectOptionType } from '../../newComponents/Select';
import Button from '../../newComponents/Button';
import Switch from '../../newComponents/Switch';

import * as S from './styles';

interface TerminalFormData {
  deviceCode: string;
  serieNfce?: string;
  serieContingencia?: string;
  proximoNumero?: string;
  cnpjEmitente?: string;
}

/** Device do PDV vindo do pdv-service, via proxy da retaguarda. */
interface PdvDevice {
  deviceCode: string;
  terminalCodes: string[];
  ativo: boolean;
  jaCadastrado: boolean;
}

/** O react-select tipa `value` como number; aqui a chave é o device_code. */
type DeviceOptionType = { value: string; label: string; isDisabled?: boolean };

const AMBIENTE_OPTIONS: SelectOptionType[] = [
  { value: 1, label: 'Produção' },
  { value: 2, label: 'Homologação' },
];

const CreateTerminalFiscal: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [ambiente, setAmbiente] = useState<SelectOptionType>(
    AMBIENTE_OPTIONS[1], // default: Homologação (2)
  );

  const [devices, setDevices] = useState<PdvDevice[]>([]);
  const [deviceSelecionado, setDeviceSelecionado] =
    useState<DeviceOptionType | null>(null);
  const [emissaoHabilitada, setEmissaoHabilitada] = useState(true);
  const [carregandoDevices, setCarregandoDevices] = useState(true);
  const [erroDevice, setErroDevice] = useState('');

  // Devices pareados no PDV. Se o pdv-service não responder (ou a empresa não
  // tiver nenhum PDV ainda), a tela cai no campo digitável — não dá para travar
  // o cadastro do terminal por indisponibilidade de outro serviço.
  useEffect(() => {
    async function carregarDevices(): Promise<void> {
      try {
        const { data } = await api.get(
          `companies/${user.empresa_id}/terminais-fiscais/devices`,
        );
        setDevices(Array.isArray(data) ? data : []);
      } catch (err) {
        setDevices([]);
      } finally {
        setCarregandoDevices(false);
      }
    }

    carregarDevices();
  }, [user.empresa_id]);

  const deviceOptions: DeviceOptionType[] = devices.map((device) => ({
    value: device.deviceCode,
    label: [
      device.deviceCode,
      device.terminalCodes.length
        ? `(caixa ${device.terminalCodes.join(', ')})`
        : '',
      device.jaCadastrado ? '— já cadastrado' : '',
      !device.ativo ? '— inativo' : '',
    ]
      .filter(Boolean)
      .join(' '),
    // Cadastrar de novo bateria na constraint de device_code único (409).
    isDisabled: device.jaCadastrado,
  }));

  const temDevices = deviceOptions.length > 0;

  const handleSubmit = useCallback(
    async (data: TerminalFormData) => {
      try {
        setIsLoading(true);
        formRef.current?.setErrors({});
        setErroDevice('');

        // Com a lista disponível, o device vem do select e não do campo de texto.
        const deviceCode = temDevices
          ? deviceSelecionado?.value || ''
          : data.deviceCode;

        if (temDevices && !deviceCode) {
          setErroDevice('Selecione o PDV');
          return;
        }

        const schema = Yup.object().shape({
          deviceCode: Yup.string().required('Informe o device code'),
          serieNfce: Yup.string().matches(
            /^\d*$/,
            'Série deve ser numérica',
          ),
          serieContingencia: Yup.string().matches(
            /^\d*$/,
            'Série de contingência deve ser numérica',
          ),
          proximoNumero: Yup.string().matches(
            /^\d*$/,
            'Número inicial deve ser numérico',
          ),
          cnpjEmitente: Yup.string()
            .transform((value) => (value === '' ? undefined : value))
            .notRequired()
            .matches(/^\d{14}$/, 'CNPJ deve conter 14 dígitos'),
        });

        await schema.validate({ ...data, deviceCode }, { abortEarly: false });

        const payload = {
          deviceCode,
          serieNfce: data.serieNfce ? Number(data.serieNfce) : undefined,
          serieContingencia: data.serieContingencia
            ? Number(data.serieContingencia)
            : undefined,
          // Número da primeira nota deste terminal (só na criação): semeia o
          // contador na atac-notas, para migração de PDV que já emitia.
          proximoNumero: data.proximoNumero
            ? Number(data.proximoNumero)
            : undefined,
          ambiente: ambiente.value,
          cnpjEmitente: data.cnpjEmitente || undefined,
          emissaoHabilitada,
        };

        await api.post(
          `companies/${user.empresa_id}/terminais-fiscais`,
          payload,
        );

        addToast({ type: 'success', title: 'Terminal fiscal criado' });
        history.push('/terminais-fiscais');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        } else {
          addToast({
            type: 'error',
            title: 'Erro ao criar terminal fiscal',
            description: (err as any)?.response?.data?.message,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      ambiente,
      emissaoHabilitada,
      user.empresa_id,
      addToast,
      history,
      temDevices,
      deviceSelecionado,
    ],
  );

  return (
    <S.Container>
      <header>
        <button
          className="previous_page"
          type="button"
          onClick={() => history.push('/terminais-fiscais')}
        >
          <IoIosArrowBack size={20} />
          Voltar
        </button>
        <div>
          <h1>Adicionar Terminal</h1>
          <p>PDV físico com série e numeração próprias para emissão de NFC-e.</p>
        </div>
      </header>

      <Divider />

      <S.CustomForm
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={{
          serieNfce: '1',
          serieContingencia: '899',
          proximoNumero: '1',
        }}
      >
        {temDevices ? (
          <div className="field">
            <label htmlFor="device-select">PDV (device code):</label>
            <Select
              id="device-select"
              options={deviceOptions}
              value={deviceSelecionado}
              onChange={(option: any) => {
                setDeviceSelecionado(option as DeviceOptionType);
                setErroDevice('');
              }}
              placeholder={
                carregandoDevices ? 'Carregando PDVs...' : 'Selecione o PDV'
              }
              isDisabled={isLoading || carregandoDevices}
              error={erroDevice}
            />
          </div>
        ) : (
          <div className="field">
            <Input
              name="deviceCode"
              label="Device Code:"
              placeholder="Ex.: CAIXA-01"
              disabled={isLoading || carregandoDevices}
            />
            <span className="field-note">
              {carregandoDevices
                ? 'Buscando os PDVs da empresa...'
                : 'Nenhum PDV pareado encontrado — informe o código manualmente.'}
            </span>
          </div>
        )}

        <div className="multi">
          <Input
            name="serieNfce"
            label="Série NFC-e:"
            placeholder="Ex.: 1"
          />
          <Input
            name="serieContingencia"
            label="Série de contingência:"
            placeholder="Ex.: 899"
          />
        </div>

        <div className="field">
          <Input
            name="proximoNumero"
            label="Número da primeira NFC-e:"
            placeholder="Ex.: 1"
          />
          <span className="field-note">
            Só definível na criação. Use um número maior que 1 quando o PDV já
            emitia por outro sistema, para continuar a sequência sem repetir
            número na SEFAZ.
          </span>
        </div>

        <div className="multi">
          <div className="field">
            <label htmlFor="ambiente-select">Ambiente:</label>
            <Select
              id="ambiente-select"
              options={AMBIENTE_OPTIONS}
              value={ambiente}
              onChange={(option: any) => setAmbiente(option as SelectOptionType)}
            />
          </div>
          <Input
            name="cnpjEmitente"
            label="CNPJ emitente (opcional):"
            placeholder="14 dígitos, sem máscara"
          />
        </div>

        <div className="field">
          <label htmlFor="emissao-habilitada">Emitir NFC-e neste terminal:</label>
          <div className="switch-field">
            <Switch
              id="emissao-habilitada"
              checked={emissaoHabilitada}
              onChange={(value: boolean) => setEmissaoHabilitada(value)}
              disabled={isLoading}
            />
            <span>{emissaoHabilitada ? 'Emite' : 'Não emite'}</span>
          </div>
          <span className="field-note">
            Desligado, as vendas deste caixa são registradas normalmente mas não
            geram NFC-e. A emissão só acontece quando a empresa E o terminal
            estiverem habilitados.
          </span>
        </div>

        <div className="buttons">
          <Button full type="submit" loading={isLoading} disabled={isLoading}>
            Salvar
          </Button>
        </div>
      </S.CustomForm>
    </S.Container>
  );
};

export default CreateTerminalFiscal;
